import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePrenotazioni } from '../hooks/usePrenotazioni';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { 
  Calendar, 
  Clock, 
  Users, 
  Phone, 
  Mail, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock as ClockIcon,
  Plus,
  Search,
  Bell,
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
  Volume2,
  VolumeX
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import CalendarView from '../components/CalendarView';

import { useToast } from '../hooks/use-toast';

const AdminDashboard = () => {
  const { logout, user } = useAuth();
  const { toast } = useToast();
  const { 
    prenotazioni, 
    prenotazioniPerData, 
    loading, 
    stats, 
    updatePrenotazione, 
    deletePrenotazione,
    addPrenotazione,
    refetch
  } = usePrenotazioni();
  
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('tutti');
  const [editingPrenotazione, setEditingPrenotazione] = useState<any>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [notificationsPermission, setNotificationsPermission] = useState<NotificationPermission>('default');

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsPermission(Notification.permission);
      
      // Restore audio enabled state from localStorage
      const savedAudioEnabled = localStorage.getItem('audioEnabled');
      if (savedAudioEnabled === 'true' && Notification.permission === 'granted') {
        setAudioEnabled(true);
      }
    }
  }, []);

  // Audio notification function
  const playNotificationSound = () => {
    if (audioEnabled) {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTuO1/DGdiIM');
      audio.play().catch(() => {
        // Silently fail if audio can't play
      });
    }
  };

  // Browser notification function
  const showBrowserNotification = (prenotazione: any) => {
    if (notificationsPermission === 'granted') {
      new Notification('Nuova Prenotazione!', {
        body: `${prenotazione.nome} ha prenotato per ${prenotazione.data} alle ${prenotazione.ora}`,
        icon: '/favicon.ico',
        tag: 'prenotazione-' + prenotazione.id
      });
    }
  };

  // Toggle notifications
  const toggleNotifications = async () => {
    if (!audioEnabled) {
      // Request permission when enabling
      if ('Notification' in window) {
        if (import.meta.env.DEV) {
          console.log('Current notification permission:', notificationsPermission);
        }
        
        if (notificationsPermission === 'default' || notificationsPermission === 'denied') {
          if (import.meta.env.DEV) {
            console.log('Requesting notification permission...');
          }
          const permission = await Notification.requestPermission();
          if (import.meta.env.DEV) {
            console.log('Permission result:', permission);
          }
          setNotificationsPermission(permission);
          
          if (permission === 'granted') {
            setAudioEnabled(true);
            localStorage.setItem('audioEnabled', 'true');
          } else if (import.meta.env.DEV) {
            console.log('Permission denied or default, notifications will not work');
          }
        } else if (notificationsPermission === 'granted') {
          setAudioEnabled(true);
          localStorage.setItem('audioEnabled', 'true');
        }
      } else {
        console.log('Notifications not supported in this browser');
        setAudioEnabled(true); // Still enable audio
        localStorage.setItem('audioEnabled', 'true');
      }
    } else {
      setAudioEnabled(false);
      localStorage.setItem('audioEnabled', 'false');
    }
  };

  // Watch for new prenotazioni to play notification
  const previousPrenotazioniCount = React.useRef(prenotazioni.length);
  const previousPrenotazioni = React.useRef<any[]>([]);
  
  useEffect(() => {
    if (prenotazioni.length > previousPrenotazioniCount.current) {
      // Find new prenotazioni
      const newPrenotazioni = prenotazioni.filter(p => 
        !previousPrenotazioni.current.some(prev => prev.id === p.id)
      );
      
      newPrenotazioni.forEach(prenotazione => {
        playNotificationSound();
        showBrowserNotification(prenotazione);
      });
    }
    previousPrenotazioniCount.current = prenotazioni.length;
    previousPrenotazioni.current = [...prenotazioni];
  }, [prenotazioni, audioEnabled, notificationsPermission]);

  // Calculate unconfirmed notifications count
  const unconfirmedCount = prenotazioni.filter(p => p.stato === 'in_attesa').length;

  // Filter prenotazioni
  const filteredPrenotazioni = prenotazioni.filter(p => {
    const matchesSearch = p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'tutti' || p.stato === statusFilter;
    const matchesDate = !selectedDate || p.data === selectedDate;
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Group filtered prenotazioni by date
  const filteredPrenotazioniPerData = filteredPrenotazioni.reduce<Record<string, any[]>>((acc, p) => {
    if (!acc[p.data]) {
      acc[p.data] = [];
    }
    acc[p.data].push(p);
    return acc;
  }, {});

  const handleStatusChange = async (id: number, newStatus: string) => {
    await updatePrenotazione(id, { stato: newStatus as any });
  };

  const handleEdit = (prenotazione: any) => {
    setEditingPrenotazione(prenotazione);
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async (updatedData: any) => {
    if (editingPrenotazione) {
      await updatePrenotazione(editingPrenotazione.id, updatedData);
      setIsEditDialogOpen(false);
      setEditingPrenotazione(null);
    }
  };

  const handleAdd = async (newData: any) => {
    await addPrenotazione(newData);
    setIsAddDialogOpen(false);
  };

  const toggleDateExpansion = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confermata':
        return <Badge className="bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="h-3 w-3" />Confermata</Badge>;
      case 'rifiutata':
        return <Badge className="bg-red-100 text-red-800 flex items-center gap-1"><XCircle className="h-3 w-3" />Rifiutata</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1"><Clock className="h-3 w-3" />In Attesa</Badge>;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confermata':
        return 'Accettata';
      case 'rifiutata':
        return 'Rifiutata';
      default:
        return 'In Attesa';
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
                         <div className="flex items-center">
               <div className="flex items-center">
                 <span className="text-2xl font-bold text-red-600">Da</span>
                 <span className="text-2xl font-bold text-black ml-1">Armando</span>
               </div>
               <span className="text-lg text-gray-600 ml-4">Gestionale Prenotazioni</span>
             </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Benvenuto, {user?.email || 'Admin'}
              </div>
              
              {/* Add Manual Booking Button */}
              <Button 
                onClick={() => setIsAddDialogOpen(true)}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-7"
              >
                <Plus className="h-3 w-3 mr-1" />
                Nuova Prenotazione
              </Button>
              
              <div className="relative">
                <Bell className="h-5 w-5 text-gray-400" />
                {unconfirmedCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {unconfirmedCount}
                  </span>
                )}
              </div>

              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleNotifications}
                className={`${
                  audioEnabled && notificationsPermission === 'granted' 
                    ? "bg-green-50 border-green-200 text-green-700" 
                    : notificationsPermission === 'denied'
                    ? "bg-red-50 border-red-200 text-red-700"
                    : ""
                }`}
                title={
                  notificationsPermission === 'denied' 
                    ? 'Permesso notifiche negato. Clicca per riprovare.' 
                    : notificationsPermission === 'default'
                    ? 'Clicca per attivare le notifiche del browser'
                    : audioEnabled 
                    ? 'Notifiche attive - Clicca per disattivare'
                    : 'Notifiche disattivate - Clicca per attivare'
                }
              >
                {audioEnabled && notificationsPermission === 'granted' ? (
                  <Volume2 className="h-4 w-4" />
                ) : (
                  <VolumeX className="h-4 w-4" />
                )}
                {notificationsPermission === 'denied' && (
                  <span className="ml-1 text-red-500">!</span>
                )}
                {notificationsPermission === 'default' && !audioEnabled && (
                  <span className="ml-1 text-yellow-500">?</span>
                )}
              </Button>

              <Button variant="outline" onClick={logout} size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Nuove Prenotazioni</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.nuove}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Confermate</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.confermate}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Rifiutate</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.rifiutate}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Oggi</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.oggi}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Users className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Totale</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totale}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="lista" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="lista" className="text-red-600">Lista Prenotazioni</TabsTrigger>
            <TabsTrigger value="calendario">Calendario</TabsTrigger>
          </TabsList>

          <TabsContent value="lista" className="space-y-6">
            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Cerca per nome o email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Filtra per stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tutti">Tutti gli stati</SelectItem>
                      <SelectItem value="in_attesa">In attesa</SelectItem>
                      <SelectItem value="confermata">Confermata</SelectItem>
                      <SelectItem value="rifiutata">Rifiutata</SelectItem>
                    </SelectContent>
                  </Select>

                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full sm:w-48"
                  />
                </div>
                
              </div>
            </div>

            {/* Prenotazioni List by Date */}
            <div className="space-y-4">
              {Object.keys(filteredPrenotazioniPerData).length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <p className="text-gray-500">Nessuna prenotazione trovata</p>
                </div>
              ) : (
                Object.entries(filteredPrenotazioniPerData)
                  .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
                  .map(([date, prenotazioniData]) => {
                    const isExpanded = expandedDates.has(date);
                    const totalPersone = prenotazioniData.reduce((sum, p) => sum + p.persone, 0);
                    const confermate = prenotazioniData.filter(p => p.stato === 'confermata').length;
                    
                    return (
                      <div key={date} className="bg-white rounded-lg shadow">
                        {/* Date Header */}
                        <div 
                          className="px-6 py-4 border-b border-gray-200 cursor-pointer"
                          onClick={() => toggleDateExpansion(date)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              )}
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {format(new Date(date), 'dd MMM yyyy', { locale: it })} - {prenotazioniData.length} prenotazioni
                                </h3>
                                <p className="text-sm text-gray-600">
                                  {totalPersone} persone totali {confermate} confermate
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Prenotazioni Table */}
                        {isExpanded && (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nome
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ora
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Persone
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contatto
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Note
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Azioni
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {prenotazioniData.map((prenotazione) => (
                                                                     <tr key={prenotazione.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {getStatusBadge(prenotazione.stato)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {prenotazione.nome}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {prenotazione.ora}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {prenotazione.persone}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      <div>
                                        <div>{prenotazione.email}</div>
                                        <div>{prenotazione.telefono}</div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {prenotazione.note || '-'}
                                    </td>
                                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                       <div className="flex items-center space-x-2">
                                         {prenotazione.stato === 'in_attesa' ? (
                                           <>
                                             <Button
                                               variant="outline"
                                               size="sm"
                                               onClick={() => handleStatusChange(prenotazione.id, 'confermata')}
                                               className="bg-green-50 border-green-200 text-green-700"
                                             >
                                               <CheckCircle className="h-4 w-4 mr-1" />
                                               Accetta
                                             </Button>
                                             <Button
                                               variant="outline"
                                               size="sm"
                                               onClick={() => handleStatusChange(prenotazione.id, 'rifiutata')}
                                               className="bg-red-50 border-red-200 text-red-700"
                                             >
                                               <XCircle className="h-4 w-4 mr-1" />
                                               Rifiuta
                                             </Button>
                                           </>
                                         ) : (
                                           <span className={`font-medium ${
                                             prenotazione.stato === 'confermata' 
                                               ? 'text-green-600' 
                                               : 'text-red-600'
                                           }`}>
                                             {getStatusText(prenotazione.stato)}
                                           </span>
                                         )}
                                       </div>
                                     </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
              )}
            </div>
          </TabsContent>

          <TabsContent value="calendario" className="space-y-6">
            <CalendarView 
              prenotazioni={prenotazioni}
              onDateSelect={setSelectedCalendarDate}
              selectedDate={selectedCalendarDate}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Prenotazione</DialogTitle>
            <DialogDescription>
              Modifica i dettagli della prenotazione
            </DialogDescription>
          </DialogHeader>
          {editingPrenotazione && (
            <EditPrenotazioneForm 
              prenotazione={editingPrenotazione} 
              onSubmit={handleSaveEdit} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuova Prenotazione</DialogTitle>
            <DialogDescription>
              Aggiungi una nuova prenotazione manualmente
            </DialogDescription>
          </DialogHeader>
          <AddPrenotazioneForm 
            onSubmit={handleAdd} 
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Form components
const EditPrenotazioneForm = ({ prenotazione, onSubmit }: { prenotazione: any, onSubmit: (data: any) => void }) => {
  const [formData, setFormData] = useState(prenotazione);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="nome">Nome</Label>
        <Input
          id="nome"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="telefono">Telefono</Label>
        <Input
          id="telefono"
          value={formData.telefono}
          onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="data">Data</Label>
          <Input
            id="data"
            type="date"
            value={formData.data}
            onChange={(e) => setFormData({ ...formData, data: e.target.value })}
            required
          />
        </div>
        
        <div>
          <Label htmlFor="ora">Ora</Label>
          <Input
            id="ora"
            type="time"
            value={formData.ora}
            onChange={(e) => setFormData({ ...formData, ora: e.target.value })}
            required
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="persone">Numero persone</Label>
        <Input
          id="persone"
          type="number"
          value={formData.persone}
          onChange={(e) => setFormData({ ...formData, persone: parseInt(e.target.value) })}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="note">Note</Label>
        <Textarea
          id="note"
          value={formData.note || ''}
          onChange={(e) => setFormData({ ...formData, note: e.target.value })}
        />
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button type="submit">Salva</Button>
      </div>
    </form>
  );
};

const AddPrenotazioneForm = ({ onSubmit }: { onSubmit: (data: any) => void }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    data: new Date().toISOString().slice(0, 10), // Default to today
    ora: '20:00', // Default to 8 PM
    persone: 2,
    note: '',
    stato: 'in_attesa' as const
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      nome: '',
      email: '',
      telefono: '',
      data: new Date().toISOString().slice(0, 10), // Reset to today
      ora: '20:00', // Reset to 8 PM
      persone: 2,
      note: '',
      stato: 'in_attesa'
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="nome">Nome</Label>
        <Input
          id="nome"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="telefono">Telefono</Label>
        <Input
          id="telefono"
          value={formData.telefono}
          onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
          required
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="data">Data</Label>
          <Input
            id="data"
            type="date"
            value={formData.data}
            onChange={(e) => setFormData({ ...formData, data: e.target.value })}
            required
          />
        </div>
        
        <div>
          <Label htmlFor="ora">Ora</Label>
          <Input
            id="ora"
            type="time"
            value={formData.ora}
            onChange={(e) => setFormData({ ...formData, ora: e.target.value })}
            required
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="persone">Numero persone</Label>
        <Input
          id="persone"
          type="number"
          min="1"
          max="20"
          value={formData.persone}
          onChange={(e) => setFormData({ ...formData, persone: parseInt(e.target.value) || 1 })}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="note">Note</Label>
        <Textarea
          id="note"
          value={formData.note}
          onChange={(e) => setFormData({ ...formData, note: e.target.value })}
        />
      </div>
      
      <div>
        <Label htmlFor="stato">Stato</Label>
        <Select value={formData.stato} onValueChange={(value) => setFormData({ ...formData, stato: value as any })}>
          <SelectTrigger>
            <SelectValue placeholder="Seleziona stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="in_attesa">In Attesa</SelectItem>
            <SelectItem value="confermata">Confermata</SelectItem>
            <SelectItem value="rifiutata">Rifiutata</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button type="submit">Aggiungi</Button>
      </div>
    </form>
  );
};

export default AdminDashboard; 